import { ContentService } from "./content-service";
import type {
  Content,
  ContentKind,
  ContentDraft,
} from "../domain/content/model";
import type { PreparedAttachment } from "../domain/capture/model";
export class ContentEditor {
  private tail: Promise<void> = Promise.resolve();
  private pendingCount = 0;
  private tagNames: string[] | undefined;
  private prepared = new Map<string, PreparedAttachment>();
  message = "草稿尚未保存";
  draftId: string = crypto.randomUUID();
  draftRevision = 0;
  failure = false;
  constructor(
    readonly service: ContentService,
    readonly kind: ContentKind,
    public value: Content,
    readonly baseRevision: number,
    readonly source: { captureId: string; revision: number } | null,
    readonly notify: () => void,
    draft?: ContentDraft,
  ) {
    if (draft) {
      this.draftId = draft.id;
      this.draftRevision = draft.revision;
      this.message = "草稿已保存";
    }
  }
  tags(names: string[]) {
    this.tagNames = names;
    return this.save();
  }
  get pending() {
    return this.pendingCount > 0;
  }
  change(patch: Partial<Content>) {
    this.value = { ...this.value, ...patch } as Content;
    return this.save();
  }
  image(p: PreparedAttachment) {
    this.prepared.set(p.metadata.id, p);
    return this.change({
      attachmentIds: [...this.value.attachmentIds, p.metadata.id],
    });
  }
  imageData(id: string) {
    return this.prepared.get(id);
  }
  save() {
    const snapshot = structuredClone(this.value),
      tags = this.tagNames ? [...this.tagNames] : undefined;
    this.pendingCount++;
    this.message = "正在保存草稿…";
    this.notify();
    const run = async () => {
      try {
        const d = await this.service.draft(
          this.kind,
          snapshot,
          this.baseRevision,
          this.draftId,
          this.draftRevision,
          this.source,
          [...this.prepared.values()],
          tags,
        );
        this.draftId = d.id;
        this.draftRevision = d.revision;
        this.failure = false;
        this.message = "草稿已保存";
      } catch (e) {
        this.failure = true;
        this.message = (e as Error).message;
        /* Image rollback must not prevent the text-only draft from committing. */ const ids =
          [];
        for (const id of snapshot.attachmentIds)
          if (await this.service.db.attachments.get(id)) ids.push(id);
        if (ids.length !== snapshot.attachmentIds.length) {
          try {
            const d = await this.service.draft(
              this.kind,
              { ...snapshot, attachmentIds: ids },
              this.baseRevision,
              this.draftId,
              this.draftRevision,
              this.source,
              [],
              tags,
            );
            this.draftId = d.id;
            this.draftRevision = d.revision;
            this.message =
              "图片未保存；文字草稿已保存。请重试图片或移除后发布。";
          } catch {
            this.message += "；文字尚未提交，请保留输入并重试或导出。";
          }
        }
      } finally {
        this.pendingCount--;
        this.notify();
      }
    };
    this.tail = this.tail.then(run);
    return this.tail;
  }
  async flush() {
    await this.tail;
    if (this.failure) throw Error(this.message);
  }
}
